// @flow
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
// Custom hooks
import { useOrderPromotionRefetch } from '../customHooks';
// Selectors
import {
  gettingOrderLoadingSelector,
  itemCountSelector,
  itemTotalSumSelector,
  itemsSelector,
  itemsWithBlendsSelector,
  lineItemTotalByUnitSelector,
  removingItemLoadingSelector,
  currentOrderBigClubInfoSelector,
  orderNumberSelector,
  orderTokenSelector,
  allPromotionsSelector,
  generalErrorsSelector,
} from '../selectors';
import { userIdSelector } from '../../user/selectors';
import {
  blendTemplatesSelector,
  getBlendTemplateItemsByBlendTemplateIdSelector,
  blendsByOrderNumberSelector,
} from '../../blend/selectors';
import {
  bigClubSettingsSelector,
  featuresConfigSelector,
} from '../../preferences/selectors';
// Actions
import {
  deleteItem,
  findOrCreateOrder,
  findItemTotalPromotions,
  initiateCheckout,
  dismissErrors,
} from '../actions';
import { getBlendTemplates, getBlends, deleteBlend } from '../../blend/actions';
// Components
import { Helmet } from 'react-helmet';
import Cart from '../components/Cart';
// Types
import type { FeaturesConfig, BigClubSettings } from '../../preferences/types';
import type { Dispatch } from '../../types';
import type { LineItem } from '../types';
// Analytics
import * as Analytics from '../../common/analytics';

type Props = {
  items: LineItem[],
  itemsWithBlendId: LineItem[],
  totalPrice: number,
  totalKg: number,
  totalUnit: number,
  loadingOrder: boolean,
  orderNumber: number,
  handleChangeQuantity: (lineItemId: number, quantity: number) => void,
  handleRemoveProduct: (lineItemId: number) => void,
  handleInitiateCheckout: () => void,
  featuresConfig: FeaturesConfig,
  bigClubSettings: BigClubSettings,
  findItemTotalPromotions: () => void,
  generalErrors: string[],
};

const CartPage = (props: Props) => {
  useEffect(() => {
    props.findOrCreateOrder();
    props.getBlendTemplates();
    Analytics.logViewCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.orderNumber) {
      props.getBlends(props.orderNumber);
    }
  }, [props.orderNumber]);

  useEffect(() => {
    props.findItemTotalPromotions();
  }, []);

  const cashbackSettings = props.bigClubSettings?.cashback;

  const blends = props.blendsByOrderNumber(props.orderNumber);

  const { isBackgroundRetry } = useOrderPromotionRefetch({
    promotions: props.promotions,
    items: props.items,
    refetchOrder: props.findOrCreateOrder,
    totalPrice: props.totalPrice,
  });

  return (
    <div>
      <Helmet>
        <title>Meu Carrinho - Bigdelivery - Seu açougue virtual</title>
        <meta
          property="og:title"
          content="Meu Carrinho - Bigdelivery - Seu açougue virtual"
        />
        <meta
          name="og:description"
          content="Carne fresca e de qualidade na porta da sua casa, na sua hora e do seu jeito."
        />
        <meta
          property="og:image"
          content={`${process.env.PUBLIC_URL}/white_logo_on_red_background.jpg`}
        />
        <meta property="og:image:type" content="image/jpg" />
        <meta property="og:image:alt" content="Bigdelivery logo" />
      </Helmet>
      <Cart
        items={props.items}
        blends={blends}
        totalPrice={props.totalPrice}
        numberOfItems={props.itemCount} // TODO: Won't this throw a length of undefined error? When there are no items.
        totalKg={props.totalKg}
        totalUnit={props.totalUnit}
        handleInitiateCheckout={props.initiateCheckout}
        loading={props.loadingOrder && !isBackgroundRetry}
        handleRemoveItem={props.handleRemoveItem}
        removingItemLoading={props.removingItemLoading}
        featuresConfig={props.featuresConfig}
        cashbackSettings={cashbackSettings}
        bigClubInfo={props.bigClubInfo}
        handleRemoveBlend={props.handleRemoveBlend}
        promotions={props.promotions}
        generalErrors={props.generalErrors}
        dismissErrors={props.dismissErrors}
        orderNumber={props.orderNumber}
        orderToken={props.orderToken}
        userId={props.userId}
      />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    items: itemsSelector(state),
    itemsWithBlendId: itemsWithBlendsSelector(state),
    itemCount: itemCountSelector(state),
    totalPrice: itemTotalSumSelector(state),
    totalKg: lineItemTotalByUnitSelector(state, 'g'),
    totalUnit: lineItemTotalByUnitSelector(state, 'un'),
    loadingOrder: gettingOrderLoadingSelector(state),
    removingItemLoading: removingItemLoadingSelector(state),
    orderNumber: orderNumberSelector(state),
    userId: userIdSelector(state),
    orderToken: orderTokenSelector(state),
    featuresConfig: featuresConfigSelector(state),
    bigClubSettings: bigClubSettingsSelector(state),
    bigClubInfo: currentOrderBigClubInfoSelector(state),
    promotions: allPromotionsSelector(state),
    blendTemplates: blendTemplatesSelector(state),
    blendTemplateItemsByTemplateId: (id: number | string) =>
      getBlendTemplateItemsByBlendTemplateIdSelector(state, id),
    blendsByOrderNumber: (orderNumber: string) =>
      blendsByOrderNumberSelector(state, orderNumber),
    generalErrors: generalErrorsSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps) => {
  return {
    getBlendTemplates: () => {
      dispatch(getBlendTemplates());
    },
    getBlends: (orderNumber: string) => {
      dispatch(getBlends(orderNumber));
    },
    findOrCreateOrder: async () => {
      const result = await dispatch(findOrCreateOrder());
      return result;
    },
    initiateCheckout: () => {
      return dispatch(initiateCheckout());
    },
    handleRemoveItem: (lineItemId: number) => {
      dispatch(deleteItem(lineItemId));
    },
    handleRemoveBlend: (blendId: number) => {
      dispatch(deleteBlend(blendId));
    },
    findItemTotalPromotions() {
      dispatch(findItemTotalPromotions());
    },
    dismissErrors: () => {
      dispatch(dismissErrors());
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(CartPage);
